import React from 'react';
import { StandardTableActions, Select, ColoredBadge } from '../../components/ui';

import { Column } from '../../components/DataTable/DataTable';
import { TruncatedText } from '../../components/DataTable/TruncatedText';
import { Project } from './useData';
import { FieldConfig } from '../../components/generic/fieldRenderers';
import { FormLayout } from '../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, formatDateTime, APP_CONFIG } from '../../config/appConfig';

const PROJECT_STATUS_COLORS: Record<string, string> = {
  'Active': '#22c55e',
  'Completed': '#3b82f6',
  'On Hold': '#eab308',
  'Cancelled': '#ef4444',
};

const DEFAULT_PROJECT_STATUS_COLOR = '#64748b';

const renderProjectStatusBadge = (statusDesc?: string | null, className?: string) => (
  <ColoredBadge
    label={statusDesc}
    color={statusDesc ? (PROJECT_STATUS_COLORS[statusDesc] ?? DEFAULT_PROJECT_STATUS_COLOR) : null}
    className={className}
  />
);

// Module Metadata
export const moduleConfig = {
  // Module Information
  module: {
    title: 'Projects',
    description: 'Manage your projects database',
    permissionModuleKey: 'projects',
    route: {
      list: '/projects',
      new: '/projects/new',
      detail: (id: number) => `/projects/${id}`,
      edit: (id: number) => `/projects/${id}/edit`,
    },
    // API Endpoints
    api: {
      list: '/crm-projects',
      detail: (id: number) => `/crm-projects/${id}`,
    },
  },

  // Pagination Settings
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },

  // Search Settings
  search: {
    placeholder: 'Search projects by name, client, or description...',
  },

  // Filter Groups Configuration
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: [
        'projectName', 'clientId', 'projectStatusId'
      ],
    },
    {
      name: 'dates',
      label: 'Dates',
      icon: '📅',
      fields: [
        'projectStartDate', 'projectEndDate', 'createdAt'
      ],
    },
  ],

  // Filter Configuration
  filters: {
    fields: [
      {
        key: 'projectName',
        label: 'Project Name',
        placeholder: 'All Project Name',
        type: 'text' as const,
        getValue: (item: Project) => item.projectName,
        options: [],
      },
      {
        key: 'clientId',
        label: 'Client',
        placeholder: 'All Clients',
        type: 'select' as const,
        getValue: (item: Project) => 
          item.client?.clientName || (item.clientId ? `Client #${item.clientId}` : ''),
        options: [],
      },
      {
        key: 'projectStatusId',
        label: 'Status',
        placeholder: 'All Status',
        type: 'select' as const,
        getValue: (item: Project) => item.projectStatus?.ps_descr || (item.projectStatusId ? `Status #${item.projectStatusId}` : ''),
        options: [],
      },
      {
        key: 'projectStartDate',
        label: 'Start Date',
        placeholder: 'All Start Date',
        type: 'date' as const,
        getValue: (item: Project) =>
          item.projectStartDate
            ? formatDateGlobal(item.projectStartDate)
            : undefined,
        options: [],
      },
      {
        key: 'projectEndDate',
        label: 'End Date',
        placeholder: 'All End Date',
        type: 'date' as const,
        getValue: (item: Project) =>
          item.projectEndDate
            ? formatDateGlobal(item.projectEndDate)
            : undefined,
        options: [],
      },
      {
        key: 'createdAt',
        label: 'Created At',
        placeholder: 'All Created At',
        type: 'date' as const,
        getValue: (item: Project) =>
          item.createdAt
            ? formatDateGlobal(item.createdAt)
            : undefined,
        options: [],
      },
    ],
    // Filter logic functions
    applyFilter: (
      items: Project[],
      filters: {
        projectName: string[] | string;
        clientId: string[];
        projectStatusId: string[];
        projectStartDate: string[];
        projectEndDate: string[];
        createdAt: string[];
      },
      dateRanges?: Record<string, { start: Date | null; end: Date | null }>
    ) => {
      const ranges = dateRanges || {};
      return items.filter((item) => {
        // Project Name filter
        const nameFilter = filters.projectName;
        if (typeof nameFilter === 'string' && nameFilter.trim() !== '') {
          const value = (item.projectName || '').toLowerCase();
          if (!value.includes(nameFilter.toLowerCase())) return false;
        } else if (Array.isArray(nameFilter) && nameFilter.length > 0) {
          const value = (item.projectName || '').toLowerCase();
          const matches = nameFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Client filter
        if (filters.clientId && filters.clientId.length > 0) {
          const displayValue = item.client?.clientName || (item.clientId ? `Client #${item.clientId}` : '');
          const matches = filters.clientId.some((filter) => {
            return displayValue === filter || 
                   (item.clientId && filter === `Client #${item.clientId}`) ||
                   (item.client?.clientName && filter === item.client.clientName);
          });
          if (!matches) return false;
        }

        // Status filter
        if (filters.projectStatusId && filters.projectStatusId.length > 0) {
          const displayValue = item.projectStatus?.ps_descr || (item.projectStatusId ? `Status #${item.projectStatusId}` : '');
          const matches = filters.projectStatusId.some((filter) => {
            return displayValue === filter ||
                   (item.projectStatusId && filter === `Status #${item.projectStatusId}`) ||
                   (item.projectStatus?.ps_descr && filter === item.projectStatus.ps_descr) ||
                   displayValue.toLowerCase().includes(filter.toLowerCase()) || 
                   filter.toLowerCase().includes(displayValue.toLowerCase());
          });
          if (!matches) return false;
        }

        // Start Date filter
        if (filters.projectStartDate.length > 0) {
          if (ranges.projectStartDate) {
            const range = ranges.projectStartDate;
            if (range.start && range.end && item.projectStartDate) {
              const itemDate = new Date(item.projectStartDate);
              if (itemDate < range.start || itemDate > range.end) return false;
            }
          } else {
            const value = item.projectStartDate
              ? formatDateGlobal(item.projectStartDate)
              : '';
            const matches = filters.projectStartDate.some((filter) => {
              return value.includes(filter) || filter.includes(value);
            });
            if (!matches) return false;
          }
        }

        // End Date filter
        if (filters.projectEndDate.length > 0) {
          if (ranges.projectEndDate) {
            const range = ranges.projectEndDate;
            if (range.start && range.end && item.projectEndDate) {
              const itemDate = new Date(item.projectEndDate);
              if (itemDate < range.start || itemDate > range.end) return false;
            }
          } else {
            const value = item.projectEndDate
              ? formatDateGlobal(item.projectEndDate)
              : '';
            const matches = filters.projectEndDate.some((filter) => {
              return value.includes(filter) || filter.includes(value);
            });
            if (!matches) return false;
          }
        }

        // Created At filter
        if (filters.createdAt.length > 0) {
          if (ranges.createdAt) {
            const range = ranges.createdAt;
            if (range.start && range.end && item.createdAt) {
              const itemDate = new Date(item.createdAt);
              if (itemDate < range.start || itemDate > range.end) return false;
            }
          } else {
            const value = item.createdAt
              ? formatDateGlobal(item.createdAt)
              : '';
            const matches = filters.createdAt.some((filter) => {
              return value.includes(filter) || filter.includes(value);
            });
            if (!matches) return false;
          }
        }

        return true;
      });
    },
  },

  // Toolbar Actions Configuration
  toolbar: {
    actions: [
      {
        key: 'download',
        icon: 'download',
        title: 'Download',
        showTitle: false,
        variant: 'default' as const,
        dropdown: [
          {
            key: 'export-excel',
            label: 'Export to Excel',
            icon: 'download',
            onClick: async (exportData?: {
              sortedData: any[];
              tableColumns: any[];
              entityName: string;
              exportFileName: string;
              formatDate?: (dateString?: string) => string;
            }) => {
              if (!exportData) {
                console.error('Export data not provided');
                return;
              }

              try {
                const { exportToExcel } = await import('../../components/exports');
                await exportToExcel(exportData);
              } catch (error: any) {
                console.error('Failed to export to Excel:', error);
                throw new Error(error?.message || 'Failed to export to Excel. Please check the console for details.');
              }
            },
          },
        ],
      },
      {
        key: 'add',
        icon: 'add',
        title: 'Add New Project',
        showTitle: false,
        variant: 'primary' as const,
        href: '/projects/new',
      },
    ],
  },

  // Table Configuration
  table: {
    actionsPosition: 'left' as const,
    onRowClick: (item: Project) => {
      window.location.href = `/projects/${item.id}`;
    },
    keyExtractor: (item: Project) => item.id,
    getClientId: (item: Project) => item.clientId ?? null,
  },

  // Empty States
  emptyStates: {
    noData: 'Get started by creating a new project.',
    noSearchResults: 'No projects found matching your search criteria.',
    noFilterResults: 'No projects match the selected filters.',
  },

  // Delete Confirmation
  delete: {
    title: 'Delete Project',
    message: 'Are you sure you want to delete this project? This action cannot be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete project. Please try again.',
    handler: async (id: number) => {
      const { deleteProject } = await import('../../services/mutations');
      await deleteProject(id);
    },
  },

  // Utility Functions
  utils: {
    formatDate: (dateString?: string) => {
      if (!dateString) return '-';
      return formatDateGlobal(dateString);
    },
    formatDateTime: (dateString?: string) => {
      if (!dateString) return '-';
      return formatDateTime(dateString);
    },
  },

  // Card View Configuration
  cardView: {
    getId: (item: Project) => item.id,
    getTitle: (item: Project) => item.projectName || `Project #${item.id}`,
    getSubtitle: (item: Project) => item.projectDescription || '',
    getStatusBadge: (item: Project) => {
      if (!item.projectStatus?.ps_descr) return undefined;
      const statusDesc = item.projectStatus.ps_descr;
      const variantMap: Record<string, 'default' | 'success' | 'warning' | 'danger' | 'info'> = {
        'Active': 'success',
        'Completed': 'info',
        'On Hold': 'warning',
        'Cancelled': 'danger',
      };
      return {
        label: statusDesc,
        variant: variantMap[statusDesc] || 'default',
      };
    },
    getCardFields: (item: Project) => {
      const fields: Array<{
        label?: string;
        value: React.ReactNode;
        icon?: React.ReactNode;
        badge?: boolean;
        badgeVariant?: 'default' | 'success' | 'warning' | 'danger' | 'info' | 'blue' | 'purple';
      }> = [];

      // Client
      if (item.client?.clientName) {
        fields.push({
          value: item.client.clientName,
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          ),
        });
      }

      // Dates
      if (item.projectStartDate || item.projectEndDate) {
        const dateStr = [
          item.projectStartDate ? formatDateGlobal(item.projectStartDate) : '',
          item.projectEndDate ? formatDateGlobal(item.projectEndDate) : '',
        ].filter(Boolean).join(' - ');
        fields.push({
          value: dateStr,
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          ),
        });
      }

      // Budget
      if (item.projectBudget !== undefined && item.projectBudget !== null) {
        fields.push({
          value: `€${item.projectBudget.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
        });
      }

      // Task Count
      if (item.taskCount !== undefined && item.taskCount !== null) {
        fields.push({
          value: `${item.taskCount} task${item.taskCount !== 1 ? 's' : ''}`,
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          ),
        });
      }

      return fields;
    },
    statusFilter: {
      enabled: true,
      defaultFilter: 'all',
      options: [
        { value: 'all', label: 'All Projects' },
        { value: 'Active', label: 'Active Only' },
        { value: 'Completed', label: 'Completed Only' },
        { value: 'On Hold', label: 'On Hold Only' },
        { value: 'Cancelled', label: 'Cancelled Only' },
      ],
      getStatus: (item: Project) => item.projectStatus?.ps_descr || 'No Status',
    },
    updateStatus: async (id: number, newStatus: any) => {
      const { updateProject } = await import('../../services/mutations');
      await updateProject(id, { projectStatusId: newStatus ? parseInt(newStatus, 10) : null });
    },
    renderStatusSelector: (item: Project, isUpdating: boolean, onStatusChange: (newStatus: any) => void) => {
      // This will need to be updated to fetch statuses dynamically
      // For now, using the projectStatus relation if available
      const currentStatusId = item.projectStatusId ? String(item.projectStatusId) : '';
      return (
        <Select
          value={currentStatusId}
          onChange={(e: any) => {
            onStatusChange(e.target.value || null);
          }}
          disabled={isUpdating}
          className="w-full text-xs"
          showClearButton={true}
          options={[{ value: '', label: 'No Status' }]}
        />
      );
    },
    actions: {
      view: true,
      edit: true,
    },
  },

  // Detail View Configuration
  detail: {
    subtitle: 'Project Details',
    notFoundTitle: 'Project not found',
    notFoundMessage: 'The project you\'re looking for doesn\'t exist.',
    notFoundBackText: 'Back to Projects',
    defaultTitle: 'Unnamed Project',
    getTitle: (item: Project) => {
      return item.projectName || `Project #${item.id}`;
    },
  },

  // Form Configuration
  form: {
    new: {
      title: 'New Project',
      subtitle: 'Add a new project to your database',
      submitText: 'Create Project',
    },
    edit: {
      title: 'Edit Project',
      submitText: 'Update Project',
    },
    cancelText: 'Cancel',
    loadingText: 'Loading project...',
    errorMessage: 'Failed to save project. Please try again.',
    create: async (data: Partial<Project>) => {
      const { createProject } = await import('../../services/mutations');
      return await createProject(data);
    },
    update: async (id: number, data: Partial<Project>) => {
      const { updateProject } = await import('../../services/mutations');
      return await updateProject(id, data);
    },
  },

  // Unified Field Configuration
  fields: [
    {
      name: 'projectName',
      label: 'Project Name',
      type: 'text',
      required: true,
      placeholder: 'Enter project name',
      validation: {
        required: true,
        minLength: 2,
        maxLength: 255,
      },
    },
    {
      name: 'clientId',
      label: 'Client',
      type: 'select',
      placeholder: 'Select a client',
      options: [],
      apiEndpoint: '/crm-clients',
      labelField: 'clientName',
      required: true,
      validation: {
        required: true,
      },
    },
    {
      name: 'projectDescription',
      label: 'Description',
      type: 'textarea',
      placeholder: 'Enter project description',
      rows: 4,
      validation: {
        maxLength: 2000,
      },
    },
    {
      name: 'projectStatusId',
      label: 'Status',
      type: 'select',
      required: true,
      placeholder: 'Select status',
      options: [], // Will be populated dynamically via API
      apiEndpoint: '/p-project-status', // API endpoint to fetch all project status options
      labelField: 'ps_descr', // Field name in API response to use as label
      validation: {
        required: true,
      },
    },
    {
      name: 'projectStartDate',
      label: 'Start Date',
      type: 'date',
      required: true,
      placeholder: 'Select start date',
      validation: {
        required: true,
      },
    },
    {
      name: 'projectEndDate',
      label: 'End Date',
      type: 'date',
      placeholder: 'Select end date',
    },
    {
      name: 'projectBudget',
      label: 'Budget',
      type: 'number',
      placeholder: 'Enter project budget',
      validation: {
        min: 0,
      },
    },
    {
      name: 'projectNotes',
      label: 'Notes',
      type: 'textarea',
      placeholder: 'Enter project notes',
      rows: 4,
      validation: {
        maxLength: 2000,
      },
    },
    {
      name: 'defaultAgreementId',
      label: 'Default Support Agreement',
      type: 'select',
      placeholder: 'Select default agreement (optional)',
      options: [],
    },
  ] as FieldConfig[],

  // Form Layout Configuration (crm-tasks style: gridCols + fieldSpans)
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'basic',
        title: 'Basic Information',
        icon: '📄',
        gridCols: 'md:grid-cols-2',
        fields: ['projectName', 'clientId', 'projectStatusId'],
        collapsible: true,
      },
      {
        id: 'description',
        title: 'Description',
        icon: '📝',
        gridCols: 'md:grid-cols-12',
        fields: ['projectDescription'],
        fieldSpans: { projectDescription: 'md:col-span-12' },
        collapsible: true,
      },
      {
        id: 'dates',
        title: 'Dates & Timeline',
        icon: '📅',
        gridCols: 'md:grid-cols-2',
        fields: ['projectStartDate', 'projectEndDate'],
        collapsible: true,
      },
      {
        id: 'financial',
        title: 'Financial',
        icon: '💰',
        gridCols: 'md:grid-cols-2',
        fields: ['projectBudget', 'defaultAgreementId'],
        collapsible: true,
      },
      {
        id: 'notes',
        title: 'Additional Information',
        icon: '📋',
        gridCols: 'md:grid-cols-12',
        fields: ['projectNotes'],
        fieldSpans: { projectNotes: 'md:col-span-12' },
        collapsible: true,
      },
    ],
  } as FormLayout,
};

// Helper function to get columns
export const getColumns = (formatDate: (dateString?: string) => string): Column<Project>[] => [
  {
    key: 'id',
    header: 'ID',
    sortable: true,
    render: (value) => (
      value || '-'
    ),
  },
  {
    key: 'projectName',
    header: 'Project Name',
    sortable: true,
    render: (value) => (
      <TruncatedText
        text={value || ''}
        maxLength={80}
        showTooltip={true}
      />
    ),
  },
  {
    key: 'client.clientName',
    header: 'Client',
    sortable: true,
    render: (value, row) => (
      <TruncatedText
        text={row.client?.clientName || value || '-'}
        maxLength={80}
        showTooltip={true}
      />
    ),
  },
  {
    key: 'projectStatus',
    header: 'Status',
    sortable: true,
    render: (_value, row) => renderProjectStatusBadge(row.projectStatus?.ps_descr),
  },
  {
    key: 'projectStartDate',
    header: 'Start Date',
    sortable: true,
    render: (value) => formatDate(value),
  },
  {
    key: 'projectEndDate',
    header: 'End Date',
    sortable: true,
    render: (value) => formatDate(value),
  },
  {
    key: 'projectBudget',
    header: 'Budget',
    sortable: true,
    render: (value) => (
      value ? `€${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'
    ),
  },
  {
    key: 'taskCount',
    header: 'Tasks',
    sortable: true,
    render: (value) => (
      value || 0
    ),
  },
  {
    key: 'createdAt',
    header: 'Created At',
    sortable: true,
    render: (value) => formatDate(value),
  },
];

// Helper function to get detail sections
export const getDetailSections = (
  item: Project,
  formatDate: (dateString?: string) => string
) => {
  const formatDateTime = moduleConfig.utils.formatDateTime;
  return [
    {
      title: 'Basic Information',
      fields: [
        {
          label: 'Project ID',
          value: item.id?.toString() || '-',
        },
        {
          label: 'Project Name',
          value: item.projectName || '-',
        },
        {
          label: 'Client',
          value: item.client?.clientName || (item.clientId ? `Client #${item.clientId}` : '-'),
        },
        {
          label: 'Status',
          value: renderProjectStatusBadge(item.projectStatus?.ps_descr),
        },
      ],
    },
    {
      title: 'Description',
      fields: [
        {
          label: 'Description',
          value: item.projectDescription ? (
            <TruncatedText
              text={item.projectDescription}
              maxLength={200}
              className="text-sm text-gray-900"
              showTooltip={true}
            />
          ) : '-',
        },
      ],
    },
    {
      title: 'Dates & Timeline',
      fields: [
        {
          label: 'Start Date',
          value: formatDate(item.projectStartDate),
        },
        {
          label: 'End Date',
          value: formatDate(item.projectEndDate),
        },
      ],
    },
    {
      title: 'Financial',
      fields: [
        {
          label: 'Budget',
          value: item.projectBudget 
            ? `€${item.projectBudget.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            : '-',
        },
        {
          label: 'Default Agreement',
          value: item.defaultAgreement?.description || (item.defaultAgreementId ? `Agreement #${item.defaultAgreementId}` : '-'),
        },
      ],
    },
    {
      title: 'Additional Information',
      fields: [
        {
          label: 'Notes',
          value: item.projectNotes || '-',
        },
        {
          label: 'Tasks Count',
          value: item.taskCount?.toString() || '0',
        },
        {
          label: 'Created At',
          value: formatDateTime(item.createdAt),
        },
        {
          label: 'Updated At',
          value: formatDateTime(item.updatedAt),
        },
      ],
    },
  ];
};

// Helper function to render toolbar icon
export const getToolbarIcon = (iconName: string): React.ReactNode => {
  switch (iconName) {
    case 'download':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
      );
    case 'add':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      );
    case 'cardboard':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
        </svg>
      );
    default:
      return null;
  }
};

// Helper function to render table action buttons
export const getTableActions = (
  item: Project,
  onDelete: (id: number) => void,
  _colors?: unknown,
  _listContext?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  return (
    <StandardTableActions
      viewTo={moduleConfig.module.route.detail(item.id)}
      editTo={moduleConfig.module.route.edit(item.id)}
      onDelete={() => onDelete(item.id)}
      canEdit={canEdit}
      canDelete={canDelete}
    />
  );
};


export const getInitialFormData = (): Partial<Project> => {
  return {
    projectName: '',
    clientId: undefined,
    projectDescription: '',
    projectStatusId: undefined,
    projectStartDate: undefined,
    projectEndDate: undefined,
    projectBudget: undefined,
    projectNotes: '',
    defaultAgreementId: undefined,
  };
};

// Helper function to map item data to form data
export const mapItemToFormData = (item: Project): Partial<Project> => {
  return {
    projectName: item.projectName || '',
    clientId: item.clientId || undefined,
    projectDescription: item.projectDescription || '',
    projectStatusId: item.projectStatusId || undefined,
    projectStartDate: item.projectStartDate || undefined,
    projectEndDate: item.projectEndDate || undefined,
    projectBudget: item.projectBudget || undefined,
    projectNotes: item.projectNotes || '',
    defaultAgreementId: item.defaultAgreementId || undefined,
  };
};

export type ModuleConfig = typeof moduleConfig;

